import { GoogleMap, Libraries, LoadScriptNext } from '@react-google-maps/api'
import { SBModelsComponent } from '@supplement-bacon/react-toolkit/models'
import { FC, useEffect, useRef } from 'react'
import { Cluster as ClusterModel } from '../../models/Cluster'
import { selectClustersByIds } from '../../store/ClusterReducer'
import { useAppSelector } from '../../store/hooks'

type Props = SBModelsComponent<ClusterModel> & {
  height?: number | string
}

const googleMapLibraries: Libraries = ['marker', 'places']

const ClusterMap: FC<Props> = ({ ids, models, height = 500 }) => {
  const items = models ? models : useAppSelector(selectClustersByIds(ids!))

  if (!items || items.length === 0) {
    return <></>
  }

  const {
    VITE_GOOGLE_MAPS_ID: GOOGLE_MAPS_ID,
    VITE_GOOGLE_MAPS_API_KEY: GOOGLE_MAPS_API_KEY,
  } = import.meta.env

  const mapRef = useRef<google.maps.Map | null>(null)
  const placesLibRef = useRef<any>(null)

  const handleMapLoad = async (map: google.maps.Map) => {
    mapRef.current = map
    try {
      placesLibRef.current = await (google.maps as any).importLibrary('places')
    } catch (error) {
      console.error('Error loading Places API:', error)
    }
  }

  const centerAndZoomMap = () => {
    if (!mapRef.current || items.length === 0) return

    const bounds = new (google.maps as any).LatLngBounds()

    items.forEach((cluster) => {
      const lat = Number(cluster.latitude) || 0
      const lng = Number(cluster.longitude) || 0
      const position = new (google.maps as any).LatLng(lat, lng)
      bounds.extend(position)
    })

    mapRef.current.fitBounds(bounds)

    // Ajuster le zoom si un seul marker
    if (items.length === 1) {
      google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
        if (mapRef.current) {
          mapRef.current.setZoom(12)
        }
      })
    }
  }

  useEffect(() => {
    // Re-centrer la map quand les clusters changent
    if (mapRef.current && items.length > 0) {
      centerAndZoomMap()
    }
  }, [items])

  return (
    <LoadScriptNext
      googleMapsApiKey={GOOGLE_MAPS_API_KEY}
      libraries={googleMapLibraries}
    >
      <GoogleMap
        key={items.map((c) => c.id).join(',')}
        mapContainerStyle={{
          width: '100%',
          height: typeof height === 'number' ? `${height}px` : height,
          borderRadius: 5,
        }}
        center={{ lat: 0, lng: 0 }}
        zoom={1}
        onLoad={(map: google.maps.Map) => {
          handleMapLoad(map)
          try {
            // cleanup previous markers if any
            const prevMarkers = (map as any)._clusterMarkers as
              | google.maps.Marker[]
              | undefined
            if (prevMarkers) {
              prevMarkers.forEach((m) => m.setMap(null))
            }

            const markers: google.maps.Marker[] = []
            const bounds = new (google.maps as any).LatLngBounds()

            items.forEach((cluster) => {
              const lat = Number(cluster.latitude) || 0
              const lng = Number(cluster.longitude) || 0
              const position = new (google.maps as any).LatLng(lat, lng)

              const marker = new (google.maps as any).Marker({
                position,
                map,
                title: cluster.name,
              })

              const infoWindow = new (google.maps as any).InfoWindow({
                content: `<div style="min-width:120px"><strong>${cluster.name}</strong><div>Venues: ${
                  cluster.venues?.length ?? 0
                }</div></div>`,
              })

              marker.addListener('click', () => {
                infoWindow.open({ anchor: marker, map })
              })

              markers.push(marker)
              bounds.extend(position)
            })

            if (items.length > 0) {
              map.fitBounds(bounds)

              // Ajuster le zoom pour un seul marker
              if (items.length === 1) {
                google.maps.event.addListenerOnce(map, 'idle', () => {
                  map.setZoom(12)
                })
              }
            }

            ;(map as any)._clusterMarkers = markers
          } catch (err) {
            console.error('Error creating cluster markers:', err)
          }
        }}
        options={{
          mapId: GOOGLE_MAPS_ID,
          streetViewControl: false,
          rotateControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      />
    </LoadScriptNext>
  )
}

type ClusterMapType = { Map: typeof ClusterMap }

export { ClusterMap, type ClusterMapType }
